import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext
} from "react";
import { MapkitProvider, useMap } from "react-mapkit";
import { Box, Flex } from "rebass/styled-components";
import useTranslation from "next-translate/useTranslation";
import I18nProvider, {
  InternalContext as NextTranslateInternalContext
} from "next-translate/I18nProvider";
import { useRouter } from "next/router";
import { RouterContext } from "next/dist/shared/lib/router-context.shared-runtime";

import {
  useSearchDispatch,
  useSearchState,
  stateToQueryParams
} from "./SearchContext";
import Icon from "../common/Icon";
import Checkbox from "../common/Checkbox";
import SearchResultCarousel from "./SearchResultCarousel";
import SearchMapSitterCard from "./SearchMapSitterCard";
import ReactDOM from "react-dom";
import { LocaleContext } from "../../components/locale";
import theme from "../../lib/theme";
import { ThemeProvider } from "styled-components";
import { ServiceType } from "../../interfaces";

interface Props {
  isMobileViewport: boolean;
  width: string;
  height: string;
  longitude: number;
  latitude: number;
  loading: boolean;
  searchResults?: any;
  setMobileMapVisible?: (boolean) => void;
  fetchMore: any;
  serviceType?: ServiceType;
  highlightedSitterIndex?: number;
}

export interface MapCoordinates {
  swLat: string;
  swLng: string;
  neLat: string;
  neLng: string;
}

const normalMarker = { "1": "/images/map-marker.svg" };
const selectedMarker = { "1": "/images/map-marker-selected.svg" };

const onMarkerSelected = event => {
  event.target.url = selectedMarker;
};

const onMarkerDeselected = event => {
  event.target.url = normalMarker;
};

const SearchMap = (props: Props) => {
  // Get shared state and dispatch from SearchContext
  const {
    isMobileViewport,
    searchResults,
    loading,
    fetchMore,
    serviceType,
    highlightedSitterIndex
  } = props;
  const localeContext = useContext(LocaleContext);
  const nextTranslateContext = useContext(NextTranslateInternalContext);

  const HEADER_HEIGHT = isMobileViewport ? 137 : 65;

  const searchDispatch = useSearchDispatch();
  const searchContextState = useSearchState();
  const { ...queryParams } = stateToQueryParams(searchContextState);
  // Internal state
  const shouldFitMapToMarkers = useRef(true);
  const [searchOnMapMoved, setSearchOnMapMoved] = useState(!isMobileViewport);

  const { t } = useTranslation("common");

  const router = useRouter();

  useEffect(() => {
    // Disable scroll on mount
    if (isMobileViewport) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      // Re-enable scroll on unmount
      if (isMobileViewport) {
        document.body.style.overflow = "visible";
      }
    };
  }, [isMobileViewport]);

  const {
    map,
    setRegion,
    mapProps: { mapRef, mapkit }
    // @ts-ignore (There is an error on mapkit-typescript types)
  } = useMap({ showsCompass: "hidden" });

  // TODO: find a better way to pass in the router context
  // without having to import RouterContext from the private next-server package
  const SitterCardAnnotation = annotation => {
    return (
      <ThemeProvider theme={theme}>
        <RouterContext.Provider value={router}>
          <I18nProvider namespaces={nextTranslateContext.ns}>
            <LocaleContext.Provider value={localeContext}>
              <SearchMapSitterCard
                caregiver={annotation}
                serviceType={serviceType}
                queryParams={queryParams}
              />
            </LocaleContext.Provider>
          </I18nProvider>
        </RouterContext.Provider>
      </ThemeProvider>
    );
  };

  const onMapMoved = useCallback(() => {
    if (!searchOnMapMoved) {
      return;
    }
    searchDispatch({
      type: "changeCoordinates",
      payload: {
        coordinates: {
          swLat: map.region.toBoundingRegion().southLatitude.toString(),
          swLng: map.region.toBoundingRegion().westLongitude.toString(),
          neLat: map.region.toBoundingRegion().northLatitude.toString(),
          neLng: map.region.toBoundingRegion().eastLongitude.toString()
        }
      }
    });
  }, [map, searchDispatch, searchOnMapMoved]);

  // Listen to map events
  useEffect(() => {
    if (!map) {
      return;
    }

    const disableAutoFittingMarkers = () => {
      // If the chebkox for searching by map is enabled
      // we should disable the auto fitting map to markers
      if (searchOnMapMoved) {
        shouldFitMapToMarkers.current = false;
      }
    };

    map.addEventListener("scroll-end", onMapMoved);
    map.addEventListener("zoom-end", onMapMoved);
    map.addEventListener("scroll-start", disableAutoFittingMarkers);
    map.addEventListener("zoom-start", disableAutoFittingMarkers);

    return () => {
      map.removeEventListener("scroll-end", onMapMoved);
      map.removeEventListener("zoom-end", onMapMoved);
      map.removeEventListener("scroll-start", disableAutoFittingMarkers);
      map.removeEventListener("zoom-start", disableAutoFittingMarkers);
    };
  }, [map, onMapMoved, searchOnMapMoved]);

  // When receiving new search results
  useEffect(() => {
    const fitMapToMarkers = () => {
      if (!map) {
        return;
      }
      map.showItems(map.annotations, {
        animate: false,
        padding: new mapkit.Padding(10, 10, 10, 10)
      });
    };
    if (!map || !mapkit || !searchResults || loading) {
      return;
    }

    // Replace map markers
    const newMarkers = [];
    for (let index = 0; index < searchResults.length; index++) {
      const r = searchResults[index];

      if (!r["latitude"] || !r["longitude"]) {
        continue;
      }
      const coordinates = new mapkit.Coordinate(
        parseFloat(r["latitude"]),
        parseFloat(r["longitude"])
      );
      const marker = new mapkit.ImageAnnotation(coordinates, {
        url: normalMarker,
        size: { width: 25, height: 30 },
        data: r,
        calloutEnabled: !isMobileViewport,
        callout: {
          calloutContentForAnnotation: annotation => {
            const domElement = document.createElement("div");
            ReactDOM.render(
              <SitterCardAnnotation {...annotation.data} />,
              domElement
            );
            return domElement;
          },
          calloutShouldAnimateForAnnotation: () => false
        }
      });

      marker.addEventListener("select", onMarkerSelected, this);
      marker.addEventListener("deselect", onMarkerDeselected, this);

      newMarkers.push(marker);
    }
    // Clear all previous markers
    map.removeAnnotations(map.annotations);

    // Add the new markers
    map.addAnnotations(newMarkers);

    // Center the map:
    // If markers - on this markers
    // If no markers - on the searched location
    if (searchResults.length === 0 && searchContextState.place) {
      setRegion({
        latitude: Number(searchContextState.place.lat),
        longitude: Number(searchContextState.place.lng),
        latitudeSpan: 0.2, // zoom
        longitudeSpan: 0.2 // zoom
      });
    } else if (shouldFitMapToMarkers.current) {
      fitMapToMarkers();
    } else {
      shouldFitMapToMarkers.current = true;
    }
  }, [
    mapkit,
    map,
    searchResults,
    loading,
    isMobileViewport,
    setRegion,
    searchContextState.place
  ]);

  // When highlighting a caregiver
  useEffect(() => {
    if (highlightedSitterIndex && map) {
      map.annotations[highlightedSitterIndex]["url"] = selectedMarker;

      map.annotations.forEach((marker, index) => {
        if (index === highlightedSitterIndex) {
          marker.url = selectedMarker;
        } else {
          if (marker.url == selectedMarker) {
            marker.url = normalMarker;
          }
        }
      });
    }
  }, [highlightedSitterIndex, map]);

  useEffect(() => {
    if (mapRef.current && isMobileViewport) {
      mapRef.current.scrollIntoView({
        behavior: "auto",
        block: "end"
      });
    }
  }, [mapRef, isMobileViewport]);

  const panToCaregiver = caregiver => {
    if (map) {
      map.annotations.forEach(marker => {
        if (marker.data.id === caregiver.id) {
          marker.selected = true;
        } else {
          marker.selected = false;
        }
      });
    }
  };

  const mapHeight = window.innerHeight - HEADER_HEIGHT;

  return (
    <Box
      sx={{
        width: "100%",
        position: ["relative", "sticky"],
        top: [0, "65px"],
        bottom: 0,
        background: "white"
      }}
      style={{ height: mapHeight }}
    >
      <Flex
        sx={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          position: "absolute",
          top: 0,
          p: [1, 2],
          zIndex: 1,
          width: "100%",
          background: isMobileViewport
            ? "linear-gradient(0deg,rgba(54,54,54,0) 0%,rgba(25, 25, 25, 0.5) 100%)"
            : ""
        }}
      >
        <Flex
          sx={{
            alignItems: "center",
            bg: "background",
            borderRadius: "default",
            p: 1,
            boxShadow: "inputFocus"
          }}
        >
          <Checkbox
            label={t("common:searchByMovingTheMap")}
            checked={searchOnMapMoved}
            onChange={event => setSearchOnMapMoved(event.target.checked)}
          />
        </Flex>
        {isMobileViewport && (
          <Box
            onClick={() => props.setMobileMapVisible(false)}
            sx={{
              alignItems: "center",
              bg: "background",
              borderRadius: "default",
              p: 1,
              boxShadow: "inputFocus",
              cursor: "pointer"
            }}
          >
            <Icon name="close" color="labradorBlack" fontSize={2} />
          </Box>
        )}
      </Flex>
      {isMobileViewport && map && mapkit && props.searchResults.length > 0 && (
        <SearchResultCarousel
          searchResults={props.searchResults}
          onVisible={panToCaregiver}
          fetchMore={fetchMore}
          loading={loading}
          serviceType={serviceType}
        />
      )}
      <Box ref={mapRef} sx={{ height: "100%", width: "100%" }} />
    </Box>
  );
};

SearchMap.defaultProps = {
  isMobileViewport: false,
  longitude: 53.8008,
  latitude: -1.5491
};

const SearchMapWithMapkitProvider = (props: Props) => (
  // TODO: move the token somewhere else
  <MapkitProvider tokenOrCallback={process.env.appleMapkitKey}>
    <SearchMap {...props} />
  </MapkitProvider>
);

export default SearchMapWithMapkitProvider;
